/**
 * Payroll and tax debts (Fan, 2026-10-27).
 *
 * Tax debts = db.tax_obligations (HMRC/DGFiP notices, payment plans and the
 * monthly PAYE + NI from payroll). Payroll = one row per month read from the
 * accountants' payroll emails (payslips + what is owed to HMRC and NEST).
 * Mounted on the Tax Reserves pages (group, single entity and BR OS tab).
 */
import { Fragment, useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, FileText, Loader2, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { payrollAPI } from '../lib/api';
import { formatCurrency } from '../lib/chartFormatters';
import { getErrorMessage } from '../lib/errors';
import { cn } from '../lib/utils';

const STATE_STYLE = {
  overdue: { label: 'Overdue', className: 'bg-red-100 text-red-800 border-red-200' },
  due: { label: 'To pay', className: 'bg-amber-100 text-amber-800 border-amber-200' },
  no_dates: { label: 'Payment plan, no dates', className: 'bg-slate-100 text-slate-700 border-slate-200' },
  paid: { label: 'Paid', className: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  cancelled: { label: 'Cancelled', className: 'bg-slate-100 text-slate-500 border-slate-200' },
};

const RUN_STATUS = {
  awaiting_approval: 'Waiting for your approval',
  submitted: 'Filed by the accountants',
};

const ENTITY_LABEL = {
  br_ltd: 'Bonjour Residences',
  bi_ltd: 'Bonjour Investments Ltd',
  eci: 'ECI',
  hsr: 'High Street Residence',
  bi_uk: 'Bonjour Invest UK',
  bh: 'Bonjour Holdings',
  bi_immo: 'Bonjour Immobilier',
  bi_sas: 'Bonjour Investments SAS',
  sci_113: 'SCI 113',
  fan_zhang_piotr_kacki: 'Fan and Piotr',
};

function StateBadge({ state, paidOn }) {
  const s = STATE_STYLE[state];
  if (!s) return <span className="text-muted-foreground">-</span>;
  return (
    <Badge variant="outline" className={cn('whitespace-nowrap', s.className)}>
      {s.label}{state === 'paid' && paidOn ? ` ${paidOn}` : ''}
    </Badge>
  );
}

function money(v, ccy = 'GBP') {
  if (v === null || v === undefined || v === '') return '-';
  return formatCurrency(Number(v), ccy);
}

function totalsLine(byCcy) {
  const parts = Object.entries(byCcy || {}).map(([ccy, v]) => money(v, ccy));
  return parts.length ? parts.join(' + ') : 'nothing';
}

async function openFile(runId, kind) {
  try {
    const res = await payrollAPI.getFile(runId, kind);
    const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    toast.error(getErrorMessage(e, 'Could not open the file'));
  }
}

function addTo(byCcy, ccy, amount) {
  const k = ccy || 'GBP';
  byCcy[k] = (byCcy[k] || 0) + (Number(amount) || 0);
}

// Tax type of a debt (Fan, 2026-10-27: "group by tax, VAT, PAYE etc").
// Stored tax_type wins; otherwise inferred from kind and label.
const TAX_ORDER = ['Corporation tax', 'VAT', 'PAYE and NI', 'VAT and PAYE arrears', 'Not a tax', 'Other'];
function taxTypeOf(o) {
  if (o.tax_type) return o.tax_type;
  if (o.kind === 'payroll_paye') return 'PAYE and NI';
  const l = `${o.label || ''}`;
  if (/\b(TVA|VAT|Umsatzsteuer)\b/i.test(l)) return 'VAT';
  if (/\bIS\b/.test(l) || /corporation tax/i.test(l)) return 'Corporation tax';
  if (/\b(PAYE|NI)\b/.test(l)) return 'PAYE and NI';
  return 'Other';
}

function toggleIn(setter, id) {
  setter((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
}

function bucket(map, list, key, make) {
  if (!map[key]) { map[key] = make(); list.push(map[key]); }
  return map[key];
}

// Overall > Taxes > Tax debts (Fan, 2026-10-27): one dropdown per company,
// like the Bookings and Invoices lists, then one dropdown per tax inside it.
// Closed rows show what is still owed.
function DebtsByCompany({ obligations }) {
  const [openIds, setOpenIds] = useState(() => new Set());
  const [openTax, setOpenTax] = useState(() => new Set());
  const groups = [];
  const byId = {};
  obligations.forEach((o) => {
    const g = bucket(byId, groups, o.entity_id, () => ({ id: o.entity_id, items: [], open: {}, overdue: {}, taxes: [], taxById: {} }));
    const type = taxTypeOf(o);
    const t = bucket(g.taxById, g.taxes, type, () => ({ id: `${o.entity_id}|${type}`, type, items: [], open: {}, overdue: {}, next: null }));
    [g, t].forEach((x) => {
      x.items.push(o);
      if (!['paid', 'cancelled'].includes(o.state)) addTo(x.open, o.currency, o.amount);
      if (o.state === 'overdue') addTo(x.overdue, o.currency, o.amount);
    });
    if (o.due_date && !['paid', 'cancelled'].includes(o.state) && (!t.next || o.due_date < t.next)) t.next = o.due_date;
  });
  groups.forEach((g) => g.taxes.sort((a, b) => TAX_ORDER.indexOf(a.type) - TAX_ORDER.indexOf(b.type)));
  const count = (n) => `${n} ${n === 1 ? 'item' : 'items'}`;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-muted-foreground border-b">
            <th className="py-2 pr-4 font-medium">Company</th>
            <th className="py-2 pr-4 font-medium">To</th>
            <th className="py-2 pr-4 font-medium text-right">Still to pay</th>
            <th className="py-2 pr-4 font-medium">Due</th>
            <th className="py-2 font-medium">Status</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => {
            const isOpen = openIds.has(g.id);
            const hasOverdue = Object.keys(g.overdue).length > 0;
            return (
              <Fragment key={g.id}>
                <tr className="border-b cursor-pointer hover:bg-muted/50" onClick={() => toggleIn(setOpenIds, g.id)} data-testid="tax-debt-company-row">
                  <td className="py-2 pr-4 font-medium">
                    <span className="inline-flex items-center gap-1">
                      {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      {ENTITY_LABEL[g.id] || g.id}
                    </span>
                    <span className="ml-2 text-xs text-muted-foreground font-normal">
                      {g.taxes.map((t) => t.type).join(', ')}
                    </span>
                  </td>
                  <td className="py-2 pr-4" />
                  <td className="py-2 pr-4 text-right tabular-nums font-medium">{totalsLine(g.open)}</td>
                  <td className="py-2 pr-4" />
                  <td className="py-2">
                    {hasOverdue && <StateBadge state="overdue" />}
                  </td>
                </tr>
                {isOpen && g.taxes.map((t) => {
                  const taxOpen = openTax.has(t.id);
                  const authorities = [...new Set(t.items.map((o) => o.authority).filter(Boolean))].join(', ');
                  const single = t.items.length === 1 ? t.items[0] : null;
                  return (
                    <Fragment key={t.id}>
                      <tr className="border-b bg-muted/20 cursor-pointer hover:bg-muted/40" onClick={() => toggleIn(setOpenTax, t.id)} data-testid="tax-debt-type-row">
                        <td className="py-1.5 pr-4 pl-6 font-medium">
                          <span className="inline-flex items-center gap-1">
                            {taxOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                            {t.type}
                          </span>
                          <span className="ml-2 text-xs text-muted-foreground font-normal">{count(t.items.length)}</span>
                        </td>
                        <td className="py-1.5 pr-4">{authorities}</td>
                        <td className="py-1.5 pr-4 text-right tabular-nums">{totalsLine(t.open)}</td>
                        <td className="py-1.5 pr-4 whitespace-nowrap">{t.next ? `next ${t.next}` : '-'}</td>
                        <td className="py-1.5">
                          {Object.keys(t.overdue).length > 0
                            ? <StateBadge state="overdue" />
                            : single && <StateBadge state={single.state} paidOn={single.paid_on} />}
                        </td>
                      </tr>
                      {taxOpen && t.items.map((o) => (
                        <tr key={o.id} className="border-b bg-muted/10 text-xs" data-testid="tax-debt-row">
                          <td className="py-1.5 pr-4 pl-12">
                            {o.label}
                            {o.tax_note && <div className="text-muted-foreground">{o.tax_note}</div>}
                          </td>
                          <td className="py-1.5 pr-4">{o.authority}</td>
                          <td className="py-1.5 pr-4 text-right tabular-nums">{money(o.amount, o.currency)}</td>
                          <td className="py-1.5 pr-4 whitespace-nowrap">{o.due_date || '-'}</td>
                          <td className="py-1.5"><StateBadge state={o.state} paidOn={o.paid_on} /></td>
                        </tr>
                      ))}
                    </Fragment>
                  );
                })}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function PayrollTaxDebtsPanel({ entityId = null, show = 'both', groupByCompany = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [openRun, setOpenRun] = useState(null);
  const [showPaid, setShowPaid] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await payrollAPI.getOverview(entityId && entityId !== 'all' ? entityId : undefined);
      setData(res.data);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not load payroll and tax debts'));
    } finally {
      setLoading(false);
    }
  }, [entityId]);

  useEffect(() => { load(); }, [load]);

  const handleScan = async () => {
    setScanning(true);
    try {
      const res = await payrollAPI.scan();
      const n = res.data?.new ?? 0;
      toast.success(n ? `Read ${n} new payroll email${n === 1 ? '' : 's'}` : 'No new payroll emails');
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Email check failed'));
    } finally {
      setScanning(false);
    }
  };

  if (loading && !data) {
    return (
      <Card data-testid="payroll-tax-debts-panel">
        <CardContent className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></CardContent>
      </Card>
    );
  }
  const obligations = (data?.obligations || []).filter((o) => showPaid || !['paid', 'cancelled'].includes(o.state));
  const paidCount = (data?.obligations || []).length - (data?.obligations || []).filter((o) => !['paid', 'cancelled'].includes(o.state)).length;
  const runs = data?.payroll_runs || [];

  return (
    <div className="space-y-6" data-testid="payroll-tax-debts-panel">
      {show !== 'payroll' && (
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle>Tax debts</CardTitle>
            <CardDescription>
              {show === 'debts' && <span className="block">Monthly PAYE and NI come from the <a href="/payroll" className="underline">Payroll</a> page.</span>}
              Still to pay: {totalsLine(data?.open_by_currency)}
              {Object.keys(data?.overdue_by_currency || {}).length > 0 && (
                <span className="text-red-700"> (overdue: {totalsLine(data?.overdue_by_currency)})</span>
              )}
            </CardDescription>
          </div>
          {paidCount > 0 && (
            <Button variant="ghost" size="sm" onClick={() => setShowPaid((v) => !v)}>
              {showPaid ? 'Hide paid' : `Show paid (${paidCount})`}
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {obligations.length === 0 ? (
            <p className="text-sm text-muted-foreground">No open tax debts.</p>
          ) : groupByCompany && !entityId ? (
            <DebtsByCompany obligations={obligations} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-muted-foreground border-b">
                    <th className="py-2 pr-4 font-medium">What</th>
                    {!entityId && <th className="py-2 pr-4 font-medium">Company</th>}
                    <th className="py-2 pr-4 font-medium">To</th>
                    <th className="py-2 pr-4 font-medium text-right">Amount</th>
                    <th className="py-2 pr-4 font-medium">Due</th>
                    <th className="py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {obligations.map((o) => (
                    <tr key={o.id} className="border-b last:border-0" data-testid="tax-debt-row">
                      <td className="py-2 pr-4">{o.label}</td>
                      {!entityId && <td className="py-2 pr-4">{ENTITY_LABEL[o.entity_id] || o.entity_id}</td>}
                      <td className="py-2 pr-4">{o.authority}</td>
                      <td className="py-2 pr-4 text-right tabular-nums">{money(o.amount, o.currency)}</td>
                      <td className="py-2 pr-4 whitespace-nowrap">{o.due_date || '-'}</td>
                      <td className="py-2"><StateBadge state={o.state} paidOn={o.paid_on} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {show !== 'debts' && (
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle>Payroll</CardTitle>
            <CardDescription>
              Read from the accountants&apos; payroll emails. HMRC is marked paid when the payment shows in the bank.
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={handleScan} disabled={scanning} data-testid="payroll-scan-btn">
            {scanning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
            Check emails
          </Button>
        </CardHeader>
        <CardContent>
          {runs.length === 0 ? (
            <p className="text-sm text-muted-foreground">No payroll emails read yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-muted-foreground border-b">
                    <th className="py-2 pr-4 font-medium">Month</th>
                    {!entityId && <th className="py-2 pr-4 font-medium">Company</th>}
                    <th className="py-2 pr-4 font-medium text-right">Gross</th>
                    <th className="py-2 pr-4 font-medium text-right">Net pay</th>
                    <th className="py-2 pr-4 font-medium text-right">HMRC</th>
                    <th className="py-2 pr-4 font-medium">HMRC status</th>
                    <th className="py-2 pr-4 font-medium text-right">Pension</th>
                    <th className="py-2 pr-4 font-medium">Pension status</th>
                    <th className="py-2 font-medium">Files</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((r) => {
                    const open = openRun === r.id;
                    const people = r.employees || [];
                    return (
                      <Fragment key={r.id}>
                        <tr className="border-b" data-testid="payroll-run-row">
                          <td className="py-2 pr-4">
                            <button type="button" className="inline-flex items-center gap-1 hover:underline disabled:no-underline"
                              onClick={() => setOpenRun(open ? null : r.id)} disabled={!people.length}>
                              {people.length ? (open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />) : <span className="w-4" />}
                              {r.label?.replace('Payroll ', '')}
                            </button>
                            <div className="text-xs text-muted-foreground pl-5">
                              {people.length ? `${people.length} people` : 'No payslips in the email'}
                              {RUN_STATUS[r.status] ? `, ${RUN_STATUS[r.status].toLowerCase()}` : ''}
                            </div>
                          </td>
                          {!entityId && <td className="py-2 pr-4">{ENTITY_LABEL[r.entity_id] || r.entity_id}</td>}
                          <td className="py-2 pr-4 text-right tabular-nums">{money(r.totals?.gross)}</td>
                          <td className="py-2 pr-4 text-right tabular-nums">{money(r.totals?.net)}</td>
                          <td className="py-2 pr-4 text-right tabular-nums">
                            {money(r.hmrc_due)}
                            <div className="text-xs text-muted-foreground">by {r.hmrc_due_date}</div>
                          </td>
                          <td className="py-2 pr-4"><StateBadge state={r.hmrc_state} paidOn={r.hmrc_paid_on} /></td>
                          <td className="py-2 pr-4 text-right tabular-nums">{money(r.pension_due)}</td>
                          <td className="py-2 pr-4"><StateBadge state={r.pension_state} paidOn={r.pension_paid_on} /></td>
                          <td className="py-2 whitespace-nowrap">
                            {r.payslips_file && (
                              <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => openFile(r.id, 'payslips')}>
                                <FileText className="h-3.5 w-3.5 mr-1" />Payslips
                              </Button>
                            )}
                            {r.p32_file && (
                              <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => openFile(r.id, 'p32')}>
                                <FileText className="h-3.5 w-3.5 mr-1" />P32
                              </Button>
                            )}
                          </td>
                        </tr>
                        {open && people.map((p) => (
                          <tr key={`${r.id}-${p.payroll_id || p.employee}`} className="border-b bg-muted/30 text-xs">
                            <td className="py-1.5 pr-4 pl-6">{p.employee}</td>
                            {!entityId && <td />}
                            <td className="py-1.5 pr-4 text-right tabular-nums">{money(p.gross)}</td>
                            <td className="py-1.5 pr-4 text-right tabular-nums">{money(p.net)}</td>
                            <td className="py-1.5 pr-4 text-right tabular-nums" colSpan={2}>
                              Tax {money(p.paye)}, NI {money(Number(p.employee_ni || 0) + Number(p.employer_ni || 0))}
                            </td>
                            <td className="py-1.5 pr-4 text-right tabular-nums" colSpan={2}>
                              {money(Number(p.employee_pension || 0) + Number(p.employer_pension || 0))}
                            </td>
                            <td />
                          </tr>
                        ))}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
              {runs.some((r) => r.hmrc_due_source === 'payslips_estimate') && (
                <p className="text-xs text-muted-foreground mt-2">
                  Where the email gave no HMRC figure, the amount is tax plus NI from the payslips, before any Employment Allowance.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      )}
    </div>
  );
}

export default PayrollTaxDebtsPanel;
